import { type CustomerId, type DeviceId, type SiteId } from '@solinkcorp/api-types';
import settings from '@solinkcorp/settings';
import { type Precision } from 'components/SidekickPage/constants';
import { type CamerasDictionaryCamera } from 'utils/types';

export interface FaceBlurQuotaData {
  expiresAt: number;
  retrievedIn: number;
  exhausted: boolean;
  usage: {
    seconds: { count: number; remaining: number };
    requests: { count: number };
  };
}

export interface SpotCheckStructuredOutput {
  title: string;
  prompt: string;
  responseFormat: string;
  states: {
    name: string;
    description: string;
    isPositiveHit?: boolean;
  }[];
  schedule: string;
  timeOfDay: {
    hours: number;
    minutes: number;
  };
  hoursOfOperation: string;
}

export type VideoSearchImageQuery =
  | { imageUri: string; cameraId?: string; timestamp?: number }
  | { cameraId: string; timestamp: number; crop: { x: number; y: number; w: number; h: number } };

export interface VideoSearchParams {
  /** Text prompt, or an image query object for image-to-video search. */
  query: string | VideoSearchImageQuery;
  startTs: number;
  endTs: number;
  count?: number;
  precision?: Precision;
  customerId: readonly string[];
  skip?: number;
  deviceId?: readonly string[];
  siteId?: readonly string[];
  cameraId?: readonly string[];
}

const VIDEO_SEARCH_STATUS_HEADER = 'X-Video-Search-Status';

/** `preparing`: a searched customer has no searchable video yet because its indexing restarted. */
export type VideoSearchStatus = 'active' | 'preparing';

export interface VideoSearchResponse {
  results: VideoSearchResult[];
  status: VideoSearchStatus;
}

/** A missing or unknown header counts as `active`, so an API without the header still works. */
function parseVideoSearchStatus(header: string | null): VideoSearchStatus {
  return header === 'preparing' ? header : 'active';
}

/**
 * Response from the image upload service. The browser PUTs the file bytes to `uploadUrl`,
 * then sends the image query (via `query.imageUri`) to the semantic search endpoint and renders `displayUrl`.
 */
export interface ImageUploadTarget {
  /** Presigned PUT URL (short-lived) to upload the raw image bytes to. */
  uploadUrl: string;
  /** Canonical s3://bucket/key URI to pass to the semantic video search. */
  imageUri: string;
  /** Presigned GET URL for rendering the image thumbnail. */
  displayUrl: string;
}

export interface VideoSearchIndexData {
  metadata: {
    __indexTimestamp: number;
    thumbnailUrl?: string; // if SemanticSearchSourceType is 'event-snapshot' or 'checkin-thumbnail' its an image, otherwise its a video resource.
  };
  locationName: string;
  resellerName: string;
  siteName: string;
  expirationTimestamp: number;
  source: SemanticSearchSourceType;
  deviceId: string;
  deviceName: string;
  customerName: string;
  resellerId: string;
  cameraId: string;
  locationId: string;
  cloudName: string;
  cloudId: string;
  customerId: string;
  siteId: SiteId;
  cameraName: string;
  timestamp: number;
  vdbSimilarityScore: number;
  snapshotUrl: string;
}

export type SemanticSearchSourceType =
  'event-snapshot' | 'checkin-thumbnail' | 'cloud-vms' | 'local-vms' | `${string}fps${string}`;

export type VideoSearchResult = VideoSearchIndexData & {
  similarResults: VideoSearchIndexData[];
};

/**
 * Spike contract for POST /cropSnapshot. Mirrors the shape defined in the
 * PlayerCropper-image-search plan; the cloud-ai backend treats this as the
 * canonical request shape. All `crop` fields are normalized to [0,1].
 */
export interface CropSnapshotRequest {
  query: {
    cameraId: string;
    /** Frame timestamp in ms since epoch. */
    timestamp: number;
    crop: { x: number; y: number; w: number; h: number };
  };
  customerId: string;
}

export interface EventSpotCheckStructuredOutput {
  title: string;
  prompt: string;
  responseFormat: string;
  states: {
    name: string;
    description: string;
    isPositiveHit?: boolean;
  }[];
  timeTarget: string;
  timestampOffset: number;
}

/** types from cloud-ai https://github.com/SolinkCorp/serverless-cloud-ai/pull/45 */
export type RequestMetrics =
  'prompt_tokens' | 'completion_tokens' | 'total_tokens' | 'images' | 'requests';

export interface UsageQuota {
  expiresAt: number;
  usage: {
    [requestMetric in RequestMetrics]: { count: number; remaining?: number };
  };
  retrievedIn: number;
  exhausted: boolean;
}
export interface IsolatedDeviceUsageQuota {
  estimatedTotalIsolatedRemainingQuota: number;
  lowestRemainingIsolatedDeviceQuota: number;
  correspondingTotalQuota: number;
  numberOfIsolatedDevices: number;
}

interface SpotCheckPreviewParameters {
  prompt: string;
  images: string[];
  states: { name: string; description: string }[];
  cameras: CamerasDictionaryCamera[];
  responseFormat: string;
  eventMetadata: Record<string, any> | undefined;
  // note: the following two fields are necessary in preview even if we build the image link client-side,
  // for generating the annotations added as text to each image which are built by the cloud-ai back-end
  timeTarget: string;
  timestampOffset: number;
}

/**
 * API for interacting with the cloud ai service.
 */
export class CloudAiApi {
  async getFaceBlurringQuota(
    jwt: string,
    customerId: string,
    deviceId: string,
  ): Promise<FaceBlurQuotaData> {
    const response = await fetch(
      `${settings.cloudAIHost}/faceBlurring/usage/quota/monthly/${customerId}/${deviceId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `BEARER ${jwt}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch face blurring quota: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Returns the structured output for a spot check template.
   */
  async getStructuredOutput(jwt: string, prompt: string): Promise<SpotCheckStructuredOutput> {
    const response = await fetch(`${settings.cloudAIHost}/chat/spot-check-template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `BEARER ${jwt}`,
      },
      // There's no current use of the images field, but we may want to pass in images in the future
      body: JSON.stringify({ prompt, images: [] }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch spot check structured output: ${response.status}`);
    }

    const data = (await response.json()) as SpotCheckStructuredOutput;

    if (data.states) {
      // Only exclude states explicitly marked as negative hits (isPositiveHit === false)
      // Keep states with isPositiveHit === true or undefined
      // (negative states might be removed in the future in which case the `isPositiveHit field would be removed)
      data.states = data.states.filter((state) => state.isPositiveHit !== false);
    }

    return data;
  }

  /**
   * Entire customer spot check quota usage.
   */
  async getCustomerSpotCheckUsage(jwt: string, customerId: string): Promise<UsageQuota> {
    const response = await fetch(
      `${settings.cloudAIHost}/openAI/usage/quota/monthly/${customerId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `BEARER ${jwt}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch spot check quota usage: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Isolated Device specific spot check quota usage.
   */
  async getIsolatedDeviceSpotCheckUsage(
    jwt: string,
    customerId: CustomerId,
    deviceIds: DeviceId[],
  ): Promise<IsolatedDeviceUsageQuota> {
    const deviceIdsParam = JSON.stringify(deviceIds);
    const response = await fetch(
      `${
        settings.cloudAIHost
      }/openAI/usage/isolatedQuotaStats/monthly/${customerId}?deviceIds=${encodeURIComponent(
        deviceIdsParam,
      )}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `BEARER ${jwt}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch isolated device spot check quota: ${response.status}`);
    }

    return await response.json();
  }

  async getEventSpotCheckPreview(jwt: string, params: SpotCheckPreviewParameters) {
    const response = await fetch(`${settings.cloudAIHost}/chat/event-spot-check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `BEARER ${jwt}`,
      },
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      throw new Error(`Failed to perform event spot check preview: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Ask cloud-ai for a cropped VMS snapshot.
   */
  async cropSnapshot(
    jwt: string,
    params: CropSnapshotRequest,
    signal?: AbortSignal,
  ): Promise<string> {
    const response = await fetch(`${settings.cloudAIHost}/cropSnapshot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `BEARER ${jwt}`,
      },
      body: JSON.stringify(params),
      signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to crop snapshot: ${response.status}`);
    }

    return await response.text();
  }

  async getSemanticVideoSearch(
    jwt: string,
    params: VideoSearchParams,
  ): Promise<VideoSearchResponse> {
    const response = await fetch(`${settings.cloudAiAlbHost}/semantic-video-search/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `BEARER ${jwt}`,
      },
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      throw new Error(`Failed to perform video search: ${response.status}`);
    }
    const results: VideoSearchResult[] = await response.json();
    return {
      results,
      status: parseVideoSearchStatus(response.headers.get(VIDEO_SEARCH_STATUS_HEADER)),
    };
  }

  /**
   * Requests a presigned target to upload a video-search image to. The backend validates
   * the content type (jpeg/png) and size, and returns the URL to PUT the bytes to along
   * with the canonical s3 URI and a displayable URL.
   */
  async requestImageUploadUrl(
    jwt: string,
    params: { fileName: string; contentType: string; size: number },
  ): Promise<ImageUploadTarget> {
    const response = await fetch(`${settings.cloudAIHost}/video-search/image-upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `BEARER ${jwt}`,
      },
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      throw new Error(`Failed to request image upload URL: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Re-mints a presigned, displayable URL for a previously uploaded image, given its s3
   * URI. Used to render the thumbnail after a page reload when the in-memory preview is
   * gone.
   */
  async getImageDisplayUrl(jwt: string, imageUri: string): Promise<string> {
    const response = await fetch(
      `${settings.cloudAIHost}/video-search/image-display-url?imageUri=${encodeURIComponent(
        imageUri,
      )}`,
      {
        method: 'GET',
        headers: {
          Authorization: `BEARER ${jwt}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to get image display URL: ${response.status}`);
    }
    const { displayUrl } = (await response.json()) as { displayUrl: string };
    return displayUrl;
  }

  /**
   * Returns the structured output for an event spot check template.
   */
  async getEventSpotCheckStructuredOutput(
    jwt: string,
    prompt: string,
  ): Promise<EventSpotCheckStructuredOutput> {
    const response = await fetch(`${settings.cloudAIHost}/chat/event-spot-check-template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `BEARER ${jwt}`,
      },
      // There's no current use of the images field, but we may want to pass in images in the future
      body: JSON.stringify({ prompt, images: [] }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch event spot check structured output: ${response.status}`);
    }

    const data = (await response.json()) as EventSpotCheckStructuredOutput;

    if (data.states) {
      // Only exclude states explicitly marked as negative hits (isPositiveHit === false)
      // Keep states with isPositiveHit === true or undefined
      // (negative states might be removed in the future in which case the `isPositiveHit field would be removed)
      data.states = data.states.filter((state) => state.isPositiveHit !== false);
    }

    return data;
  }
}

export default new CloudAiApi();
